import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router';

/**
 * Editorial Carousel showcasing books dynamically from the backend.
 * Renders only when real books are present in stock.
 */
export default function HeroShowcaseCarousel({ items = [] }) {
  const scrollRef = useRef(null);
  const scrollRafRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const scrollByAmount = (direction) => {
    if (!scrollRef.current) return;
    const amount = direction === 'left' ? -320 : 320;
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (!scrollRef.current || scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      if (!scrollRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll > 0) {
        setScrollProgress(scrollLeft / maxScroll);
      }
    });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        el.removeEventListener('scroll', handleScroll);
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
      };
    }
  }, [items]);

  // If no books exist in backend, do not render hardcoded mock items
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="bg-paper border-b border-rule pt-6 pb-12 sm:pt-8 sm:pb-16 overflow-hidden">
      <div className="page-gutter mx-auto max-w-(--container-page)">
        {/* Top Header Bar matching Reference Layout */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-rule">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-stamp animate-pulse inline-block"></span>
              <span className="font-mono text-xs uppercase tracking-widest text-ink-60 font-semibold">
                Chapters&amp;Co Catalog &middot; Books &amp; Merchandise
              </span>
            </div>
            <h2 className="text-display-lg font-display text-ink tracking-tight max-w-[20ch]">
              Curated books &amp; <span className="text-stamp">goods</span> for your desk.
            </h2>
          </div>

          <div className="md:text-right flex flex-col items-start md:items-end gap-3">
            <p className="text-ink-80 text-sm max-w-[36ch] leading-relaxed">
              Curated editions direct from press alongside bespoke bookstore wares. Scroll sideways to walk through our collection.
            </p>
            {/* Carousel Arrow Controls */}
            <div className="flex items-center gap-3 mt-1">
              <button
                onClick={() => scrollByAmount('left')}
                aria-label="Previous items"
                className="w-9 h-9 rounded-full border border-rule hover:border-ink hover:bg-paper-raised flex items-center justify-center transition-colors text-ink cursor-pointer"
              >
                &larr;
              </button>
              <span className="font-mono text-xs tracking-wider text-ink-60 uppercase">
                Scroll to move &rarr;
              </span>
              <button
                onClick={() => scrollByAmount('right')}
                aria-label="Next items"
                className="w-9 h-9 rounded-full border border-rule hover:border-ink hover:bg-paper-raised flex items-center justify-center transition-colors text-ink cursor-pointer"
              >
                &rarr;
              </button>
            </div>
          </div>
        </div>

        {/* Horizontal Carousel Track */}
        <div
          ref={scrollRef}
          className="flex gap-6 sm:gap-8 overflow-x-auto py-8 no-scrollbar cursor-grab active:cursor-grabbing"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {items.map((item, idx) => {
            const time = `08:${String((idx * 20) % 60).padStart(2, '0')}`;
            const price = item.priceMinor
              ? `Rs. ${(item.priceMinor / 100).toLocaleString()}`
              : item.price || '';
            const cover = item.cover?.url || item.coverImage?.url || item.coverUrl;
            const link = `/new/${item.slug || item.id || ''}`;
            const bgColors = ['#e44322', '#346b96', '#657e33', '#bd6ca8', '#cc6a36', '#226bb8'];
            const color = item.color || bgColors[idx % bgColors.length];

            return (
              <Link
                key={item.slug || item.id || idx}
                to={link}
                className="flex-none w-[240px] sm:w-[260px] md:w-[280px] group no-underline text-inherit"
              >
                {/* Product Poster Plate with Contrast Color */}
                <div
                  style={{ backgroundColor: color }}
                  className="w-full aspect-[4/5] rounded-lg p-3 sm:p-4 flex flex-col items-center justify-center relative shadow-md group-hover:shadow-xl transition-[transform,box-shadow] duration-200 transform group-hover:-translate-y-1 overflow-hidden"
                >
                  {cover ? (
                    <img
                      src={cover}
                      alt={item.title}
                      loading="lazy"
                      className="w-full h-full object-contain rounded shadow-md transform group-hover:scale-105 transition-transform duration-200"
                    />
                  ) : (
                    <div className="text-white/80 font-serif text-center px-4">
                      <span className="text-sm font-semibold block">{item.title}</span>
                      <span className="text-xs opacity-75 mt-1 block">{item.author}</span>
                    </div>
                  )}
                </div>

                {/* Information Row underneath the poster */}
                <div className="mt-4 flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-xs text-stamp font-bold">
                        {item.time || time}
                      </span>
                      <h3 className="font-sans font-bold text-base text-ink group-hover:text-stamp transition-colors truncate max-w-[160px]">
                        {item.title}
                      </h3>
                    </div>
                    <span className="font-mono text-xs font-bold text-ink-80 shrink-0">
                      {price}
                    </span>
                  </div>

                  <p className="text-ink-80 text-xs leading-relaxed line-clamp-2">
                    {item.blurb || item.synopsis || item.description || ''}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Timeline Bottom Progress Bar */}
        <div className="pt-6 border-t border-rule flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 w-full sm:w-1/2">
            <span className="font-mono text-[11px] text-stamp font-bold">08:00</span>
            <div className="relative flex-1 h-0.5 bg-rule rounded-full overflow-hidden">
              <div
                className="absolute top-0 bottom-0 left-0 bg-stamp transition-[width] duration-150 rounded-full"
                style={{ width: `${Math.max(12, scrollProgress * 100)}%` }}
              />
            </div>
            <span className="font-mono text-[11px] text-ink-60">10:00</span>
          </div>

          <div className="text-right">
            <p className="font-mono text-[11px] text-ink-60 tracking-wider uppercase">
              Risograph &amp; Editions &middot; Hand-curated by Chapters&amp;Co
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
